import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { BaseViewModel } from '../../../../models/base/base-view-model';
import { CompanyDomainModel } from '../../../../domainModels/company-domain-model';
import { BannerMessage } from '../../../../models/company/dto/banner-message';
import { CustomerMessage } from '../../../../models/company/dto/customer-message';
import { DistinctUtils } from '../../../../utils/distinct-utils';
import { DomSanitizer } from '@angular/platform-browser';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { Injectable, SecurityContext } from '@angular/core';

export interface PageBannerRow {
  id: string;
  safeHtml: string;
  isActive: boolean;
}

@Injectable()
export class PageBannerViewModel extends BaseViewModel {
  constructor(
    private readonly companyDomainModel: CompanyDomainModel,
    private readonly sanitizer: DomSanitizer
  ) {
    super();
  }

  // How long each message stays on screen before cycling to the next.
  static readonly ROTATION_INTERVAL_MS = 30 * 1000;

  public readonly company$ = this.companyDomainModel.company$;

  public readonly companyMessages$: Observable<CustomerMessage | undefined> = this.company$.pipe(
    map(company => company?.messages),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Tick to drop expired messages even when company$ hasn't changed.
  private readonly expiryRefresh$ = timer(0, PageBannerViewModel.ROTATION_INTERVAL_MS);

  public readonly activeMessages$: Observable<BannerMessage[]> = combineLatest([
    this.companyMessages$,
    this.expiryRefresh$
  ]).pipe(
    map(([messages]) => messages?.getActiveMessagesInPriorityOrder() ?? []),
    distinctUntilChanged(DistinctUtils.distinctUniquelyIdentifiableArray),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * 0 messages emits null; 1 message emits once; N messages rotate every ROTATION_INTERVAL_MS.
   * Rotation restarts whenever the active list changes.
   */
  public readonly currentBannerMessage$: Observable<BannerMessage | null> = this.activeMessages$.pipe(
    switchMap(messages => {
      if (!messages || messages.length === 0) return of<BannerMessage | null>(null);
      if (messages.length === 1) return of<BannerMessage | null>(messages[0]);
      return timer(0, PageBannerViewModel.ROTATION_INTERVAL_MS).pipe(map(tick => messages[tick % messages.length]));
    }),
    distinctUntilChanged(DistinctUtils.distinctUniquelyIdentifiable),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // All active rows precomputed so the template can stack them in one grid cell — height matches the tallest message.
  public readonly bannerRows$: Observable<PageBannerRow[]> = combineLatest([
    this.activeMessages$,
    this.currentBannerMessage$
  ]).pipe(
    map(([messages, current]) => {
      const currentId = current?.getUniqueIdentifier();
      return messages.map(msg => ({
        id: msg.getUniqueIdentifier(),
        safeHtml: this.sanitizer.sanitize(SecurityContext.HTML, msg.message) ?? '',
        isActive: msg.getUniqueIdentifier() === currentId
      }));
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasBanner$: Observable<boolean> = this.bannerRows$.pipe(
    map(rows => rows.length > 0),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}
